import React, { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlinePlus, HiOutlineChevronRight, HiOutlineTag, HiOutlineCloudDownload, HiOutlineRefresh, HiOutlineExclamation, HiOutlineEyeOff, HiOutlineSparkles, HiOutlineTrash } from "react-icons/hi";
import { PiClockCounterClockwise, PiCheckBold } from "react-icons/pi";
import { useHistory } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { CONFIG_STATUS, CONFIG_STATUS_LABEL, readListState, writeListState, needsVerification, TUBE_TYPE_LABELS, isShowingDeleted } from "../../utils";

import API from "../../api";
import { capture } from "../../instrument";
import useSelection from "../../hooks/useSelection";
import BulkActionBar from "../../components/selection/BulkActionBar";
import PageLoadingSpinner from "../../components/PageLoadingSpinner";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useHotkeys } from "react-hotkeys-hook";
import useListShortcuts from "../../hooks/useListShortcuts";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";
import { rankBySearch } from "../../utils/search";
import useSavedViews from "../../hooks/useSavedViews";
import useSearchFilters, { SEARCH_OPERATOR } from "../../hooks/useSearchFilters";
import SearchFilters from "../../components/SearchFilters";
import SaveViewModal from "../../components/SaveViewModal";
import ViewTabs from "../../components/ViewTabs";

const DEFAULT_VIEWS = [
  { _id: "ALL", name: "Tous", filters: [] },
  { _id: "NEW", name: "Nouveaux", filters: [{ id: "default-new", field: "syncStatus", operator: SEARCH_OPERATOR.IS, values: ["CREE"] }] },
  { _id: "UPDATED", name: "Mis à jour", filters: [{ id: "default-updated", field: "syncStatus", operator: SEARCH_OPERATOR.IS, values: ["MIS_A_JOUR", "CONFLIT"] }] },
  { _id: "MISSING", name: "Disparus", filters: [{ id: "default-missing", field: "syncStatus", operator: SEARCH_OPERATOR.IS, values: ["DELETED"] }] },
];

const STORAGE_KEY = "code-list-state";

const Code = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const savedState = useMemo(() => readListState(STORAGE_KEY), []);
  const [search, setSearch] = useState(savedState?.search || "");
  const [showDeleted, setShowDeleted] = useState(isShowingDeleted(savedState?.filters));

  const {
    data: codes = [],
    isLoading: isLoadingCodes,
    isError: isErrorCodes,
  } = useQuery({
    queryKey: ["codes", { showDeleted }],
    queryFn: async () => {
      const response = await API.get(`/code?includeDeleted=${showDeleted}`);
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }
      return response.data;
    },
  });

  const { data: tables = [] } = useQuery({
    queryKey: ["tables"],
    queryFn: async () => {
      const response = await API.get("/table");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });

  const isLoadingData = isLoadingCodes;

  const tableCountByCodeId = useMemo(() => {
    const counts = {};
    for (const table of tables) {
      if (!table.codes) continue;
      for (const code of table.codes) {
        const codeId = code._id || code;
        counts[codeId] = (counts[codeId] || 0) + 1;
      }
    }
    return counts;
  }, [tables]);

  const { data: allOverrides = [] } = useQuery({
    queryKey: ["sync-overrides"],
    queryFn: async () => {
      const response = await API.get("/sync/override");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });
  const conflictDocIds = useMemo(() => new Set(allOverrides.filter((o) => o.conflict).map((o) => String(o.docId))), [allOverrides]);
  const overrideDocIds = useMemo(() => new Set(allOverrides.map((o) => String(o.docId))), [allOverrides]);

  const filterFields = useMemo(() => {
    const uniqueCodeTypes = [...new Set(codes.map((c) => c.codeType).filter(Boolean))].sort();
    const uniqueTypes = [...new Set(codes.map((c) => c.type).filter(Boolean))].sort();
    const uniqueSyncSources = [...new Set(codes.flatMap((c) => c._sources?.map((s) => s.source)).filter(Boolean))].sort();
    const uniqueTubeLabels = [...new Set(codes.flatMap((c) => (c.tubes || []).map((t) => TUBE_TYPE_LABELS[t.type])).filter(Boolean))].sort();
    const uniqueTubeCounts = [...new Set(codes.map((c) => String((c.tubes || []).reduce((sum, t) => sum + (t.quantity || 1), 0))))].sort((a, b) => Number(a) - Number(b));

    return [
      { key: "codeType", label: "Type de code", operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT], values: uniqueCodeTypes, resolve: (code) => code.codeType || "" },
      { key: "type", label: "Type", operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT], values: uniqueTypes },
      {
        key: "tubeType",
        label: "Tube",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: uniqueTubeLabels,
        resolve: (code) => (code.tubes || []).map((t) => TUBE_TYPE_LABELS[t.type]).filter(Boolean),
      },
      {
        key: "tubeCount",
        label: "Nombre de tubes",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: uniqueTubeCounts,
        resolve: (code) => String((code.tubes || []).reduce((sum, t) => sum + (t.quantity || 1), 0)),
      },
      {
        key: "hasTubes",
        label: "Tubes renseignés",
        operators: [SEARCH_OPERATOR.IS],
        values: ["Oui", "Non"],
        resolve: (code) => (code.tubes?.length > 0 ? "Oui" : "Non"),
      },
      {
        key: "syncStatus",
        label: "Status synchronisation",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: ["CONFLIT", "CREE", "MIS_A_JOUR", "MODIFIE", "DELETED", "SYNCED"],
        resolve: (code) => {
          const statuses = [];
          if (conflictDocIds.has(String(code._id))) statuses.push("CONFLIT");
          if (code.configStatus === CONFIG_STATUS.CREATED) statuses.push("CREE");
          if (code.configStatus === CONFIG_STATUS.UPDATED) statuses.push("MIS_A_JOUR");
          if (overrideDocIds.has(String(code._id))) statuses.push("MODIFIE");
          if (code._sources?.some((s) => s.missingFromSource)) statuses.push("DELETED");
          if (statuses.length === 0 && code._sources?.length) statuses.push("SYNCED");
          return statuses;
        },
      },
      {
        key: "syncSource",
        label: "Source synchronisation",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: uniqueSyncSources,
        resolve: (code) => code._sources?.map((s) => s.source).filter(Boolean) || [],
      },
      {
        key: "enabledAtFrontDesk",
        label: "Disponible à la saisie",
        operators: [SEARCH_OPERATOR.IS],
        values: ["Oui", "Non"],
        resolve: (code) => (code.enabledAtFrontDesk !== false ? "Oui" : "Non"),
      },
      {
        key: "hasTable",
        label: "Relié à une table",
        operators: [SEARCH_OPERATOR.IS],
        values: ["Oui", "Non"],
        resolve: (code) => (tableCountByCodeId[code._id] > 0 ? "Oui" : "Non"),
      },
      {
        key: "deleted",
        label: "Supprimé",
        operators: [SEARCH_OPERATOR.IS],
        values: ["Oui", "Non"],
        resolve: (code) => (code.isDeleted ? "Oui" : "Non"),
      },
    ];
  }, [codes, tableCountByCodeId, conflictDocIds, overrideDocIds]);

  const { filters: activeFilters, setFilters: setActiveFilters, applyFilters } = useSearchFilters(filterFields, { initialFilters: savedState?.filters });

  const onFiltersChange = (nextFilters) => {
    setActiveFilters(nextFilters);
    setShowDeleted(isShowingDeleted(nextFilters));
    resetSelection();
  };

  const { savedViews, activeViewId, saveModalOpen, setSaveModalOpen, handleSelectView, isViewActive, handleConfirmSaveView, handleDeleteView, saveOptions } = useSavedViews({
    collectionName: "code",
    defaultViews: DEFAULT_VIEWS,
    activeFilters,
    setActiveFilters: onFiltersChange,
    initialViewId: savedState?.viewId,
  });

  useEffect(() => {
    writeListState(STORAGE_KEY, { search, filters: activeFilters, viewId: activeViewId });
  }, [search, activeFilters, activeViewId]);

  const filterCounts = useMemo(() => {
    const created = codes.filter((doc) => doc.configStatus === CONFIG_STATUS.CREATED).length;
    const updated = codes.filter((doc) => doc.configStatus === CONFIG_STATUS.UPDATED || conflictDocIds.has(String(doc._id))).length;
    const missing = codes.filter((doc) => doc._sources?.some((s) => s.missingFromSource)).length;
    return { all: codes.length, created, updated, missing };
  }, [codes, conflictDocIds]);

  const getViewBadge = (tab) => {
    if (tab._id === "ALL") return <span className="ml-1.5 text-xs text-gray-400">{filterCounts.all}</span>;
    if (tab._id === "NEW" && filterCounts.created > 0)
      return <span className="ml-1.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-xs text-orange-600">{filterCounts.created}</span>;
    if (tab._id === "UPDATED" && filterCounts.updated > 0)
      return <span className="ml-1.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-xs text-orange-600">{filterCounts.updated}</span>;
    if (tab._id === "MISSING" && filterCounts.missing > 0) return <span className="ml-1.5 text-xs text-gray-400">{filterCounts.missing}</span>;
    return null;
  };

  const filteredCodes = useMemo(() => {
    const alphabetical = [...applyFilters(codes)].sort((a, b) => (a.value || "").localeCompare(b.value || ""));
    return rankBySearch(alphabetical, search, (code) => [code.value, code.name, code.UMLS, code.sampleId].filter(Boolean).join(" "));
  }, [codes, search, activeFilters, conflictDocIds, overrideDocIds]);

  const listRef = useRef(null);
  const selectedIndexRef = useRef(savedState?.selectedIndex || 0);
  const [selectedIndex, setSelectedIndex] = useState(savedState?.selectedIndex || 0);

  const virtualizer = useVirtualizer({
    count: filteredCodes.length,
    getScrollElement: () => listRef.current,
    initialOffset: savedState?.scrollTop || 0,
    estimateSize: () => 53,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  const resetSelection = () => {
    selectedIndexRef.current = 0;
    setSelectedIndex(0);
    virtualizer.scrollToIndex(0);
    writeListState(STORAGE_KEY, { scrollTop: 0, selectedIndex: 0 });
  };

  const handleNavigateDown = () => {
    const next = selectedIndexRef.current >= filteredCodes.length - 1 ? 0 : selectedIndexRef.current + 1;
    selectedIndexRef.current = next;
    setSelectedIndex(next);
    virtualizer.scrollToIndex(next);
  };

  const handleNavigateUp = () => {
    const prev = selectedIndexRef.current <= 0 ? filteredCodes.length - 1 : selectedIndexRef.current - 1;
    selectedIndexRef.current = prev;
    setSelectedIndex(prev);
    virtualizer.scrollToIndex(prev);
  };

  const onRowMouseMove = (event, index) => {
    const pointerDidNotMove = event.movementX === 0 && event.movementY === 0;
    if (pointerDidNotMove || selectedIndexRef.current === index) return;
    selectedIndexRef.current = index;
    setSelectedIndex(index);
  };

  useListShortcuts(
    {
      onUp: handleNavigateUp,
      onDown: handleNavigateDown,
      onOpen: ({ newTab } = {}) => {
        const code = filteredCodes[selectedIndexRef.current];
        if (!code) return;
        if (newTab) window.open(`/code/${code._id}/edit`, "_blank");
        else navigateToCode(code);
      },
    },
    [filteredCodes, virtualizer],
  );

  useHotkeys(SHORTCUTS.CREATE.keys, () => history.push("/code/add"), { preventDefault: true });

  const navigateToCode = (code) => {
    const ids = filteredCodes.map((c) => c._id);
    writeListState(STORAGE_KEY, { scrollTop: listRef.current.scrollTop, selectedIndex: ids.indexOf(code._id) });
    history.push({ pathname: `/code/${code._id}/edit`, state: { filteredIds: ids } });
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    resetSelection();
  };

  const orderedIds = useMemo(() => filteredCodes.map((c) => c._id), [filteredCodes]);
  const selection = useSelection(orderedIds);
  const selectedNeedsVerification = useMemo(() => codes.some((c) => selection.selected.has(c._id) && needsVerification(c)), [codes, selection.selected]);
  const selectionHasDeleted = useMemo(() => codes.some((c) => selection.selected.has(c._id) && c.isDeleted), [codes, selection.selected]);

  const bulkMutation = useMutation({
    mutationFn: ({ action, update }) => API.post("/code/bulk", { ids: Array.from(selection.selected), action, update }),
    onSuccess: (response, variables) => {
      if (!response.ok) return toast.error(response.message || "Une erreur est survenue");
      const { processed = 0, skipped = 0 } = response.data || {};
      const past = { delete: "supprimé(s)", verify: "vérifié(s)", update: "mis à jour" }[variables.action];
      let message = `${processed} code(s) ${past}`;
      if (skipped > 0) message += ` — ${skipped} ignoré(s)`;
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ["codes"] });
      queryClient.invalidateQueries({ queryKey: ["tables"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      selection.clear();
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  useHotkeys("x", () => selection.select(selectedIndexRef.current), { preventDefault: true, enabled: filteredCodes.length > 0 }, [filteredCodes.length]);
  useHotkeys("mod+a", (e) => { e.preventDefault(); selection.selectAll(); }, { preventDefault: true }, [orderedIds]);
  useHotkeys("escape", () => selection.clear(), { enabled: selection.count > 0 }, [selection.count]);

  if (isLoadingData) {
    return <PageLoadingSpinner />;
  }

  if (isErrorCodes) {
    return (
      <div className="flex min-h-[calc(100vh-60px)] flex-col items-center justify-center gap-3 bg-gray-50">
        <span className="text-sm text-red-500">Erreur lors du chargement des codes</span>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-60px)] bg-gray-50 p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-semibold text-gray-900">Codes</h1>
          <ViewTabs
            defaultViews={DEFAULT_VIEWS}
            savedViews={savedViews}
            isViewActive={isViewActive}
            onSelectView={handleSelectView}
            onDeleteView={handleDeleteView}
            getViewBadge={getViewBadge}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
            onClick={() => window.open("/audit-log/code2", "_blank", "noopener,noreferrer,width=1000,height=800")}
            title="Journal d'audit"
          >
            <PiClockCounterClockwise className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={() => {
              history.push("/code/add");
              notifyShortcut("CREATE");
            }}
          >
            <HiOutlinePlus className="h-4 w-4" />
            <span>Nouveau code</span>
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <SearchFilters
          fields={filterFields}
          filters={activeFilters}
          onFiltersChange={onFiltersChange}
          search={search}
          onSearchChange={handleSearchChange}
          placeholder="Rechercher un code..."
          saveOptions={saveOptions}
        />

        <div ref={listRef} className="max-h-[calc(100vh-252px)] overflow-y-auto">
          {filteredCodes.length > 0 && (
            <div style={{ height: totalHeight, position: "relative" }}>
              {virtualItems.map((virtualRow) => {
                const code = filteredCodes[virtualRow.index];
                const count = tableCountByCodeId[code._id] || 0;
                const isMissing = !code.isDeleted && code._sources?.some((s) => s.missingFromSource);
                const isConflict = !code.isDeleted && conflictDocIds.has(String(code._id));
                const hasStateChip = isMissing || isConflict || needsVerification(code);
                const isSelected = selection.isSelected(code._id);
                return (
                  <div
                    key={code._id}
                    data-active={virtualRow.index === selectedIndex || undefined}
                    onMouseMove={(event) => onRowMouseMove(event, virtualRow.index)}
                    data-selected={isSelected || undefined}
                    style={{ position: "absolute", top: 0, left: 0, right: 0, height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                    className="group flex cursor-pointer items-center border-b border-gray-100 pr-4 hover:bg-gray-50 data-[active=true]:bg-primary-50/50 data-[selected=true]:bg-primary-50"
                    onClick={() => navigateToCode(code)}
                  >
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={isSelected}
                      aria-label={`Sélectionner ${code.value}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        selection.select(virtualRow.index, { shiftKey: e.shiftKey });
                      }}
                      className="group/check flex shrink-0 items-center self-stretch pl-4 pr-3"
                    >
                      <span
                        className={`flex h-[18px] w-[18px] items-center justify-center rounded-md border transition-all ${
                          isSelected ? "border-primary-500 bg-primary-500 text-white shadow-sm" : "border-gray-300 bg-white text-transparent group-hover/check:border-primary-400"
                        } ${selection.count > 0 || isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                      >
                        <PiCheckBold className="h-2.5 w-2.5" />
                      </span>
                    </button>
                    <span className="min-w-0 truncate text-sm font-medium text-gray-900">{code.value}</span>

                    {code.name && <span className="ml-2 min-w-0 truncate text-sm text-gray-500">{code.name}</span>}

                    <div className="ml-auto flex flex-shrink-0 items-center gap-3 pl-4">
                      {code.enabledAtFrontDesk === false && <HiOutlineEyeOff className="h-3.5 w-3.5 text-gray-400" title="Non disponible à la saisie" />}
                      <div className="flex items-center gap-1.5">
                        {code.isDeleted && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            <HiOutlineTrash className="h-3 w-3" />
                            Supprimé
                          </span>
                        )}
                        {!code.isDeleted && needsVerification(code) && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-orange-200 bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700">
                            {code.configStatus === CONFIG_STATUS.CREATED ? <HiOutlineSparkles className="h-3 w-3" /> : <HiOutlineRefresh className="h-3 w-3" />}
                            {CONFIG_STATUS_LABEL[code.configStatus]}
                          </span>
                        )}
                        {isConflict && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                            <HiOutlineExclamation className="h-3 w-3" />
                            Conflit
                          </span>
                        )}
                        {isMissing && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                            <HiOutlineTrash className="h-3 w-3" />
                            Supprimé
                          </span>
                        )}
                        {!code.isDeleted && !hasStateChip && code._sources?.length > 0 && (
                          <HiOutlineCloudDownload className="h-3.5 w-3.5 text-primary-500" title="Synchronisé" />
                        )}
                      </div>
                      <span className="hidden max-w-[140px] truncate text-xs text-gray-400 sm:block">
                        {[code.codeType === "groupe" ? "Groupe" : code.codeType === "analyse" ? "Analyse" : null, code.type].filter(Boolean).join(" - ")}
                      </span>
                      {code.sampleId && <span className="hidden max-w-[100px] truncate text-xs text-gray-400 md:block">{code.sampleId}</span>}
                      {code.UMLS && <span className="hidden max-w-[120px] truncate text-xs text-gray-400 lg:block">{code.UMLS}</span>}
                      <span className="w-14 text-right text-xs tabular-nums text-gray-400">
                        {count} table{count !== 1 ? "s" : ""}
                      </span>
                      <HiOutlineChevronRight className="h-4 w-4 text-gray-300" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {filteredCodes.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <HiOutlineTag className="mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm">{search || activeFilters.some((f) => f.values.length > 0) ? "Aucun resultat pour les filtres appliques" : "Aucun code"}</p>
              {!search && activeFilters.length === 0 && <p className="mt-1 text-xs">Creez votre premier code pour commencer</p>}
            </div>
          )}
        </div>
      </div>

      <BulkActionBar
        count={selection.count}
        total={filteredCodes.length}
        noun="code"
        loading={bulkMutation.isPending}
        onClear={selection.clear}
        edits={[
          {
            key: "codeType",
            label: "Type de code",
            options: [
              { value: "groupe", label: "Groupe" },
              { value: "analyse", label: "Analyse" },
              { value: "", label: "Aucun" },
            ],
            onSelect: (value) => bulkMutation.mutate({ action: "update", update: { codeType: value } }),
          },
          {
            key: "type",
            label: "Type",
            options: [
              { value: "DEFAULT", label: "DEFAULT" },
              { value: "SANG", label: "SANG" },
              { value: "ECHANTILLON", label: "ECHANTILLON" },
              { value: "H24", label: "H24" },
              { value: "SEROLOGIE", label: "SEROLOGIE" },
              { value: "PCR", label: "PCR" },
              { value: "SELLE", label: "SELLE" },
              { value: "AUTRE", label: "AUTRE" },
            ],
            onSelect: (value) => bulkMutation.mutate({ action: "update", update: { type: value } }),
          },
        ]}
        actions={[
          {
            key: "verify",
            label: "Marquer vérifié",
            variant: "primary",
            hidden: !selectedNeedsVerification || selectionHasDeleted,
            onClick: () => bulkMutation.mutate({ action: "verify" }),
          },
          {
            key: "delete",
            label: "Supprimer",
            variant: "danger",
            hidden: selectionHasDeleted,
            confirm: {
              title: `Supprimer ${selection.count} code(s) ?`,
              body: "Les codes référencés par des tables seront ignorés. Cette action est irréversible.",
              confirmLabel: "Supprimer",
            },
            onClick: () => bulkMutation.mutate({ action: "delete" }),
          },
        ]}
      />

      {saveModalOpen && (
        <SaveViewModal
          onClose={() => setSaveModalOpen(false)}
          onConfirm={(name) => {
            setSaveModalOpen(false);
            handleConfirmSaveView(name);
          }}
        />
      )}
    </div>
  );
};

export default Code;
